'use client'

import clsx from 'clsx'
import { motion } from 'framer-motion'
import { forwardRef, useState, type ComponentPropsWithoutRef } from 'react'
import { MinusIcon } from '../../icons/MinusIcon'
import { PlusIcon } from '../../icons/PlusIcon'
import { xStack, yStack } from '../../patterns/stack'
import { sprinkles } from '../../theme/sprinkles.css'
import { tokens } from '../../theme/tokens.css'
import { framerTransitions } from '../../theme/transitions'
import { Collapsible } from '../Collapsible/Collapsible'
import { PerilColorIcon } from '../PerilColorIcon/PerilColorIcon'
import { PerilCoverageList } from '../PerilCoverageList/PerilCoverageList'
import { Text } from '../Text/Text'
import {
  cell,
  collapsibleContent,
  collapsibleTrigger,
  triggerIconBase,
  triggerIconVariants,
} from './ComparisonTable.css'

type TitleDataCellProps = Omit<ComponentPropsWithoutRef<'td'>, 'children'> & {
  title: string
  description?: string
  perilColor?: string | null
  coverageList?: Array<string>
}

export function TitleDataCell({
  className,
  title,
  description,
  perilColor,
  coverageList,
  ...props
}: TitleDataCellProps) {
  const [open, setOpen] = useState(false)

  const cellTitle = (
    <div className={xStack({ gap: 'xs', alignItems: 'center' })}>
      {perilColor && <PerilColorIcon color={perilColor} />}
      <Text as="span" size={{ _: 'xs', lg: 'md' }} color="textPrimary">
        {title}
        {description && (
          <>
            <PlusIcon
              color={tokens.legacyColors.textSecondary}
              className={clsx(triggerIconBase, triggerIconVariants.closed)}
            />
            <MinusIcon
              color={tokens.legacyColors.textSecondary}
              className={clsx(triggerIconBase, triggerIconVariants.open)}
            />
          </>
        )}
      </Text>
    </div>
  )
  const children = description ? (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger className={collapsibleTrigger}>{cellTitle}</Collapsible.Trigger>
      <Collapsible.Content
        className={clsx(collapsibleContent, perilColor && sprinkles({ pl: 'xl' }))}
        asChild={true}
        // SEO: Make sure content gets mounted despite being collapsed
        forceMount={true}
      >
        <AnimatedTitleDataCellContent open={open} description={description} coverageList={coverageList} />
      </Collapsible.Content>
    </Collapsible.Root>
  ) : (
    cellTitle
  )

  return (
    <td className={clsx(cell, className)} {...props}>
      {children}
    </td>
  )
}

type AnimatedTitleDataCellProps = { open: boolean } & Pick<
  TitleDataCellProps,
  'description' | 'coverageList' | 'className'
>

const AnimatedTitleDataCellContent = forwardRef<HTMLDivElement, AnimatedTitleDataCellProps>(
  ({ open, description, coverageList, className }, ref) => {
    return (
      <motion.div
        ref={ref}
        className={className}
        initial={false}
        transition={{
          transition: framerTransitions.easeInOutCubic,
          duration: framerTransitions.defaultDuration,
        }}
        variants={{
          opened: {
            // custom property reference: https://www.radix-ui.com/docs/primitives/components/collapsible
            height: 'var(--radix-collapsible-content-height)',
          },
          closed: {
            height: 0,
          },
        }}
        animate={open ? 'opened' : 'closed'}
      >
        <div className={yStack({ gap: 'md', py: 'xxs' })}>
          <Text as="p" size={{ _: 'xs', lg: 'md' }} color="textSecondary" balance={true}>
            {description}
          </Text>
          {coverageList && (
            <PerilCoverageList
              className={sprinkles({ fontSize: { _: 'xs', lg: 'md' } })}
              items={coverageList}
              variant="secondary"
            />
          )}
        </div>
      </motion.div>
    )
  },
)
AnimatedTitleDataCellContent.displayName = 'AnimatedTitleDataCell'
